/**
 * Macro #20 — Moderation / Admin API client.
 *
 * Read-only reads carry the SIWE session cookie. Every privileged/owner mutation
 * additionally attaches the memory-only session-bound CSRF token (shared with
 * `territory-identity-api`) and, on a stale-token 403, refetches once and retries
 * — never optimistic. The server (SIWE session + env allowlist) is authoritative;
 * `is_admin` is NEVER a client-side security decision.
 */
import { API_BASE } from "./api";
import { clearCsrfToken, getCsrfToken } from "./territory-identity-api";

export class ModerationApiError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ModerationApiError";
    this.status = status;
    this.code = code;
  }
}

export type TargetType = "territory" | "artwork";
export type CaseStatus = "MODERATED" | "REVIEW_PENDING" | "RESTORED" | "INHERITED";

export interface TimelineEvent {
  type: string;
  actor_role: string;
  at: string;
  note?: string;
  reason_category?: string;
  reason_label?: string;
}

export interface OwnerCase {
  case_id: string;
  target_type: TargetType;
  target_id: string;
  status: CaseStatus;
  active: boolean;
  reason_category: string;
  reason_label: string | null;
  admin_note: string | null;
  emergency: boolean;
  review_pending: boolean;
  review_count: number;
  created_at: string | null;
  updated_at: string | null;
  restored_at: string | null;
  restore_note: string | null;
  root_case_id: string | null;
  inherited_from_case_id: string | null;
  timeline: TimelineEvent[];
}

export interface AdminCase extends OwnerCase {
  owner: string | null;
  actor_wallet: string | null;
  revision: number;
  inherited_into_case_id: string | null;
  support_count?: number;
  owner_message_count?: number;
  awaiting_reply?: boolean;
  admin_unread?: boolean;
}

export interface SupportMessage {
  message_id: string;
  case_id: string;
  sender_role: "owner" | "admin";
  body: string;
  created_at: string | null;
}

export interface OwnerTargetView {
  moderated: boolean;
  case: OwnerCase | null;
  support_thread: SupportMessage[];
  /** Macro #20 (a/b/c) — owner has unread admin activity on this target's case. */
  owner_unread?: boolean;
}

export interface ModerationTarget {
  target_type: TargetType;
  target_id: string;
}

export interface Notifications {
  owner_unread: boolean;
  admin_unread: boolean;
  /** Targets (territory identity_id / artwork_id) with unread owner activity. */
  owner_unread_targets: ModerationTarget[];
}

export interface AuditRow {
  action: string;
  actor_wallet: string;
  target_type: string;
  target_id: string;
  case_id: string;
  root_case_id?: string | null;
  reason_category?: string | null;
  before?: unknown;
  after?: unknown;
  created_at: string | null;
}

export interface CaseDetail {
  case: AdminCase;
  support_thread: SupportMessage[];
  audit: AuditRow[];
  lineage: AdminCase[];
}

export interface Capabilities {
  wallet: string;
  is_admin: boolean;
}

export interface ReasonCategory {
  key: string;
  label: string;
}

interface ErrorEnvelope {
  code?: string;
  message?: string;
  detail?: { code?: string; message?: string };
}

async function parse<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    const code = env.code ?? env.detail?.code ?? "UNKNOWN";
    const message = env.message ?? env.detail?.message ?? resp.statusText;
    throw new ModerationApiError(resp.status, code, message);
  }
  return body as T;
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
    signal,
  });
  return parse<T>(resp);
}

async function csrfPost<T>(path: string, body: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
  const payload = JSON.stringify(body);
  const doPost = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
      body: payload,
      signal,
    });
  let resp = await doPost(await getCsrfToken());
  if (resp.status === 403) {
    const peek = (await resp.clone().json().catch(() => null)) as ErrorEnvelope | null;
    const code = peek?.code ?? peek?.detail?.code;
    if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doPost(await getCsrfToken(true));
    }
  }
  return parse<T>(resp);
}

function idem(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export const moderationApi = {
  // ── capability (any authenticated wallet) ──────────────────────────────────
  capabilities: (signal?: AbortSignal) => get<Capabilities>("/api/v1/me/capabilities", signal),

  // ── unread notifications for the global top bar (glow badge) ────────────────
  notifications: (signal?: AbortSignal) =>
    get<Notifications>("/api/v1/moderation/notifications", signal),

  // ── admin reads ─────────────────────────────────────────────────────────────
  reasonCategories: (signal?: AbortSignal) =>
    get<{ reason_categories: ReasonCategory[] }>("/api/v1/admin/moderation/reason-categories", signal),
  summary: (signal?: AbortSignal) =>
    get<{ moderated: number; review_pending: number; restored: number; awaiting_reply: number }>(
      "/api/v1/admin/moderation/summary",
      signal,
    ),
  listCases: (
    opts: { status?: string; targetType?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams();
    if (opts.status) q.set("status", opts.status);
    if (opts.targetType) q.set("target_type", opts.targetType);
    if (opts.limit) q.set("limit", String(opts.limit));
    const qs = q.toString();
    return get<{ cases: AdminCase[]; count: number }>(
      `/api/v1/admin/moderation/cases${qs ? `?${qs}` : ""}`,
      signal,
    );
  },
  caseDetail: (caseId: string, signal?: AbortSignal) =>
    get<CaseDetail>(`/api/v1/admin/moderation/cases/${encodeURIComponent(caseId)}`, signal),
  search: (q: string, signal?: AbortSignal) =>
    get<{ cases: AdminCase[] }>(`/api/v1/admin/moderation/search?q=${encodeURIComponent(q)}`, signal),
  recentAudit: (limit = 50, signal?: AbortSignal) =>
    get<{ audit: AuditRow[]; count: number }>(`/api/v1/admin/moderation/audit?limit=${limit}`, signal),

  // ── admin mutations ───────────────────────────────────────────────────────
  hide: (input: {
    target_type: TargetType;
    target_id: string;
    reason_category: string;
    admin_note: string;
    emergency?: boolean;
  }) =>
    csrfPost<{ case: AdminCase }>("/api/v1/admin/moderation/hide", {
      ...input,
      emergency: !!input.emergency,
      idempotency_key: idem(),
    }),
  keep: (caseId: string, adminNote: string) =>
    csrfPost<{ case: AdminCase }>(
      `/api/v1/admin/moderation/cases/${encodeURIComponent(caseId)}/keep`,
      { admin_note: adminNote, idempotency_key: idem() },
    ),
  restore: (caseId: string, adminNote?: string) =>
    csrfPost<{ case: AdminCase }>(
      `/api/v1/admin/moderation/cases/${encodeURIComponent(caseId)}/restore`,
      { admin_note: adminNote ?? null, idempotency_key: idem() },
    ),
  adminSupportReply: (caseId: string, body: string) =>
    csrfPost<{ message: SupportMessage }>(
      `/api/v1/admin/moderation/cases/${encodeURIComponent(caseId)}/support/reply`,
      { body, idempotency_key: idem() },
    ),
  markAdminRead: (caseId: string) =>
    csrfPost<{ ok: boolean }>(
      `/api/v1/admin/moderation/cases/${encodeURIComponent(caseId)}/read`,
      {},
    ),

  // ── owner-facing ────────────────────────────────────────────────────────────
  targetView: (targetType: TargetType, targetId: string, signal?: AbortSignal) =>
    get<OwnerTargetView>(
      `/api/v1/moderation/target/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}`,
      signal,
    ),
  requestReview: (caseId: string) =>
    csrfPost<{ case: OwnerCase }>(
      `/api/v1/moderation/cases/${encodeURIComponent(caseId)}/request-review`,
      { idempotency_key: idem() },
    ),
  readSupport: (caseId: string, signal?: AbortSignal) =>
    get<{ case_id: string; support_thread: SupportMessage[]; closed: boolean }>(
      `/api/v1/moderation/cases/${encodeURIComponent(caseId)}/support`,
      signal,
    ),
  ownerSupport: (caseId: string, body: string) =>
    csrfPost<{ message: SupportMessage }>(
      `/api/v1/moderation/cases/${encodeURIComponent(caseId)}/support`,
      { body, idempotency_key: idem() },
    ),
  markOwnerRead: (caseId: string) =>
    csrfPost<{ ok: boolean }>(
      `/api/v1/moderation/cases/${encodeURIComponent(caseId)}/support/read`,
      {},
    ),
};
